import {
  TemplateHeader,
  PatientDetailsSection,
  TemplateFooter,
} from './TemplateShared';
import { SignedUrlPreview } from './SignedUrlPreview';
import { DASH, formatLongDate } from './templateUtils';
import type { TemplatePatient } from './templateUtils';

export interface ImagingTemplateImage {
  id: string;
  signedUrl?: string;
}

export interface ImagingTemplateDetail {
  id: string;
  title?: string;
  date?: string;
  imagingType?: string;
  bodyPart?: string;
  alternateBodyPartName?: string;
  comments?: string;
  images: ImagingTemplateImage[];
}

export interface ImagingTemplateProps {
  details: ImagingTemplateDetail[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightDetailId?: string;
}

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex items-start gap-3 border-b border-zinc-200 px-4 py-2.5 last:border-b-0">
    <p className="w-32 shrink-0 text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
      {label}
    </p>
    <p className="flex-1 text-[11px] leading-tight whitespace-pre-line text-zinc-950">
      {value?.trim() || DASH}
    </p>
  </div>
);

const ImagingCard = ({
  detail,
  index,
  highlightDetailId,
}: {
  detail: ImagingTemplateDetail;
  index: number;
  highlightDetailId?: string;
}) => {
  const isHighlighted = !!highlightDetailId && detail.id === highlightDetailId;
  return (
    <article
      id={`imaging-${detail.id}`}
      className={`flex flex-col overflow-hidden rounded-sm border-2 ${
        isHighlighted ? 'border-amber-400' : 'border-gray-200'
      } bg-white pb-4`}
    >
      <header className="flex flex-col gap-1.5 bg-slate-700 px-5 pt-5 pb-3 text-white">
        <p className="text-[10px] leading-tight font-bold tracking-[0.35px] uppercase opacity-80">
          Study {index + 1}
        </p>
        <p className="text-[14px] leading-tight font-semibold">
          {detail.title?.trim() || 'Imaging Study'}
        </p>
        <p className="text-[10px] leading-tight">
          <span className="font-semibold">Date:</span>{' '}
          {formatLongDate(detail.date)}
        </p>
      </header>

      <div className="mx-5 mt-3 overflow-hidden rounded-sm border border-zinc-200 bg-white">
        <InfoRow label="Imaging Type" value={detail.imagingType} />
        <InfoRow label="Body Part" value={detail.bodyPart} />
        <InfoRow
          label="Alternate Body Part"
          value={detail.alternateBodyPartName}
        />
        <InfoRow label="Comments" value={detail.comments} />
      </div>

      {detail.images.length > 0 ? (
        <div className="mx-5 mt-3 flex flex-col gap-2">
          <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
            Images
          </p>
          <div className="grid">
            {detail.images.map((image, imageIndex) => (
              <div
                key={image.id}
                className="flex h-48 w-full items-center justify-center overflow-hidden rounded-sm border border-zinc-200 bg-zinc-100"
              >
                <SignedUrlPreview
                  signedUrl={image.signedUrl}
                  title={`Imaging preview ${imageIndex + 1}`}
                  unavailableLabel="Image unavailable"
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
};

export const ImagingTemplate = ({
  details,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightDetailId,
}: ImagingTemplateProps) => {
  const items = details ?? [];
  const generated = generatedAt ?? new Date();

  return (
    <div className="mx-auto flex min-h-[1056px] w-[816px] flex-col bg-white font-sans text-black">
      <TemplateHeader />

      <section className="flex flex-col gap-4 bg-gray-100 px-8 pt-8 pb-5">
        <h1 className="text-[28px] leading-tight font-bold text-black">
          Imaging Report
        </h1>
        <PatientDetailsSection patient={patient} />
      </section>

      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        {items.length === 0 ? (
          <div className="rounded-sm border-2 border-gray-200 bg-white px-5 py-12 text-center text-[12px] text-zinc-500">
            No imaging studies available
          </div>
        ) : (
          items.map((detail, index) => (
            <ImagingCard
              key={detail.id}
              detail={detail}
              index={index}
              highlightDetailId={highlightDetailId}
            />
          ))
        )}
      </section>

      <TemplateFooter
        filename={filename}
        generatedAt={generated}
        pageInfo={pageInfo}
      />
    </div>
  );
};
